import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import { X } from "lucide-react";
import AppButton from "../../../common_components/AppButton";
import CustomLoader from "../../../common_components/CustomLoader";
import { NumberInput } from "../../../common_components/FormInputs";
import { StyledCurrency } from "../../../helper/StyledCurrency";
import { formatStat } from "../../../helper/statsFormatter";
/** Same geometry as the module's other modal buttons — see `GenerateInvoiceModal`'s
 *  note on why `color="inherit"` is required to escape the global `containedPrimary`
 *  rule, which would otherwise force these pink. */
const BUTTON_SX = {
    minWidth: 110,
    height: 36,
    minHeight: 36,
    px: "14px",
    borderRadius: "8px",
    textTransform: "capitalize",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap",
    boxShadow: "0px 1px 2px 0px #0A0D120D",
};
/**
 * Column track shared by the header and every row, so the three columns line up
 * without a real table. Collapses to two columns on narrow screens, dropping the
 * read-only Invoice Amount rather than the input.
 */
const ROW_GRID = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr 200px", sm: "1.4fr 1fr 220px" },
    gap: "12px",
    alignItems: "center",
};
/** Cells hidden at the `xs` breakpoint, where the grid drops to two columns. */
const SM_ONLY = { display: { xs: "none", sm: "block" } };
/**
 * "Adjust Total Bill" — hand-enter a Total Bill for each checked client.
 *
 * Opened from the listing's "Update Total Bill" action, which only appears while the
 * Invoice Mismatch filter is applied: an override exists to reconcile a row whose
 * invoice doesn't agree with its computed bill.
 *
 * Blanking a field is a real instruction, not an incomplete one — it clears the
 * override and returns that row to its computed figure — so there's nothing to
 * validate here and Submit is always live.
 */
const AdjustTotalBillModal = ({ open, entries: initialEntries, onSubmit, onClose, loading = false, }) => {
    const [entries, setEntries] = useState([]);
    /**
     * Save failures are shown in the dialog rather than as a toast: this dialog sits at
     * `zIndex: 2000` (below), which is above `ToastProvider`'s snackbar layer, so a toast
     * raised while it's open would render behind it. Same reasoning as the Development
     * Hours repeater.
     */
    const [error, setError] = useState(null);
    // Re-seed each time it opens so a previous selection's typed amounts never leak in.
    useEffect(() => {
        if (!open)
            return;
        setEntries(initialEntries.map((entry) => ({ ...entry })));
        setError(null);
    }, [open, initialEntries]);
    const updateEntry = useCallback((id, totalBill) => {
        setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, totalBill } : entry)));
        // Typing is the fix, so a previous failure notice clears as soon as the user starts.
        setError(null);
    }, []);
    const handleSubmit = useCallback(async () => {
        setError(null);
        const saved = await onSubmit(entries);
        if (saved === false) {
            setError("Couldn't update the Total Bill. Please try again.");
        }
    }, [entries, onSubmit]);
    if (!open)
        return null;
    return (_jsx(Dialog, { open: open, maxWidth: "md", fullWidth: true, 
        // Same rule as the module's other dialogs: only the explicit controls dismiss
        // it, so typed amounts can't be lost to a stray backdrop click or Esc.
        disableEscapeKeyDown: true, sx: {
            zIndex: 2000,
            "& .MuiBackdrop-root": {
                backgroundColor: "rgba(0,0,0,0.5)",
                backdropFilter: "blur(3px)",
            },
            "& .MuiDialog-container > .MuiPaper-root": { borderRadius: "12px", maxWidth: "760px" },
        }, children: _jsxs(DialogContent, { sx: { p: 0 }, children: [_jsxs(Box, { sx: {
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                        px: 3,
                        py: 2,
                        borderBottom: "1px solid #EAECF0",
                    }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: { fontSize: "18px", fontWeight: 600, color: "#101828", lineHeight: "28px" }, children: "Adjust Total Bill" }), _jsx(Typography, { sx: { fontSize: "14px", color: "#667085", lineHeight: "20px", mt: 0.25 }, children: entries.length === 1
                                        ? "Set the amount to bill for this client."
                                        : `Set the amount to bill for each of these ${entries.length} clients.` })] }), _jsx(IconButton, { onClick: onClose, disabled: loading, sx: { width: 36, height: 36, color: "#98A2B3", "&:hover": { color: "#475467", bgcolor: "#F9FAFB" } }, children: _jsx(X, { size: 20 }) })] }), _jsxs(Box, { sx: { ...ROW_GRID, px: 3, pt: 2, pb: 1 }, children: [_jsx(Typography, { sx: { fontSize: "13px", fontWeight: 600, color: "#252B37" }, children: "Client" }), _jsx(Typography, { sx: { fontSize: "13px", fontWeight: 600, color: "#252B37", ...SM_ONLY }, children: "Invoice Amount" }), _jsx(Typography, { sx: { fontSize: "13px", fontWeight: 600, color: "#252B37" }, children: "Total Bill" })] }), _jsx(Box, { sx: { px: 3, pb: 2, maxHeight: "58vh", overflowY: "auto" }, children: entries.map((entry) => (_jsxs(Box, { sx: {
                            ...ROW_GRID,
                            py: "10px",
                            borderTop: "1px solid #F2F4F7",
                        }, children: [_jsx(Typography, { sx: {
                                    fontSize: "14px",
                                    color: "#27272A",
                                    // A long client name shortens rather than widening the column and
                                    // pushing the input off the row.
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }, title: entry.client, children: entry.client }), _jsx(Box, { sx: SM_ONLY, children: _jsx(StyledCurrency, { value: formatStat(entry.invoiceAmount, "amount"), fontSize: "14px", valueColor: "#27272a", symbolColor: "#a1a1aa" }) }), _jsx(NumberInput, { value: entry.totalBill, onChange: (e) => updateEntry(entry.id, e.target.value), placeholder: "Enter Amount", min: 0, size: "sm", disabled: loading })] }, entry.id))) }), error && (_jsx(Box, { sx: { px: 3, pb: 2 }, children: _jsx(Alert, { severity: "error", onClose: () => setError(null), sx: { borderRadius: "10px", alignItems: "center", fontSize: "13px" }, children: error }) })), _jsxs(Box, { sx: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1.5,
                        flexWrap: "wrap",
                        px: 3,
                        py: 2,
                        borderTop: "1px solid #EAECF0",
                    }, children: [_jsx(AppButton, { variant: "contained", color: "inherit", disabled: loading, onClick: onClose, sx: {
                                ...BUTTON_SX,
                                bgcolor: "#fff",
                                color: "#414651",
                                border: "1px solid #D5D7DA",
                                "&:hover": { bgcolor: "#F9FAFB", border: "1px solid #D5D7DA" },
                            }, children: "Cancel" }), _jsx(AppButton, { variant: "contained", color: "inherit", disabled: loading, onClick: handleSubmit, sx: {
                                ...BUTTON_SX,
                                bgcolor: "#181D27",
                                color: "#fff",
                                border: "1px solid #181D27",
                                "&:hover": { bgcolor: "#101828", border: "1px solid #101828" },
                            }, children: loading ? _jsx(CustomLoader, { color: "inherit", size: 18 }) : "Submit" })] })] }) }));
};
export default AdjustTotalBillModal;
